import { useMemo, type ReactNode } from "react";
import {
  IndianRupee,
  ShoppingCart,
  Store,
  Package,
  Clock,
  ArrowRight,
  Truck,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Bar, BarChart, CartesianGrid, Cell, LabelList, Pie, PieChart, XAxis, YAxis } from "recharts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from "@/components/ui/chart";
import { cn } from "@/lib/utils";
import {
  useAdminDeliveryPartners,
  useAdminOrders,
  useAdminProducts,
  useAdminStores,
  usePendingStores,
  useAdminIncomeSummary,
} from "@/hooks/queries";

const statusColor: Record<string, string> = {
  placed: "bg-info/10 text-info border-info/20",
  confirmed: "bg-warning/10 text-warning border-warning/20",
  dispatched: "bg-primary/10 text-primary border-primary/20",
  delivered: "bg-success/10 text-success border-success/20",
  cancelled: "bg-destructive/10 text-destructive border-destructive/20",
};

// Order-status donut: state colours, always paired with a labelled legend.
const statusChart: { key: string; label: string; color: string }[] = [
  { key: "placed", label: "Placed", color: "hsl(var(--info))" },
  { key: "confirmed", label: "Confirmed", color: "hsl(var(--warning))" },
  { key: "dispatched", label: "Dispatched", color: "hsl(262 60% 58%)" },
  { key: "delivered", label: "Delivered", color: "hsl(var(--primary))" },
  { key: "cancelled", label: "Cancelled", color: "hsl(var(--destructive))" },
];

const inr = (n: number, fractionDigits = 2) =>
  `₹${n.toLocaleString("en-IN", { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits })}`;

const Panel = ({
  title,
  action,
  className,
  children,
}: {
  title: string;
  action?: ReactNode;
  className?: string;
  children: ReactNode;
}) => (
  <section
    className={cn(
      "glass-card flex min-h-0 flex-col rounded-xl border border-border p-3",
      className,
    )}
  >
    <header className="mb-2 flex shrink-0 items-center justify-between gap-2">
      <h2 className="text-sm font-semibold text-foreground">{title}</h2>
      {action}
    </header>
    <div className="min-h-0 flex-1">{children}</div>
  </section>
);

const KpiTile = ({
  title,
  value,
  icon: Icon,
  tone = "bg-primary/10 text-primary",
}: {
  title: string;
  value: string | number;
  icon: LucideIcon;
  tone?: string;
}) => (
  <div className="flex min-w-0 flex-col gap-1.5 glass-card rounded-xl border border-border px-3 py-2.5 sm:flex-row sm:items-center sm:gap-3">
    <div className={cn("flex h-8 w-8 shrink-0 items-center justify-center rounded-lg sm:h-9 sm:w-9", tone)}>
      <Icon className="h-4 w-4" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-medium leading-tight text-muted-foreground sm:truncate">{title}</p>
      <p className="truncate font-heading text-lg font-bold leading-tight tracking-tight text-foreground tabular-nums">
        {value}
      </p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const { data: ordersData, isLoading: ordersLoading } = useAdminOrders();
  const { data: storesData, isLoading: storesLoading } = useAdminStores();
  const { data: pendingStores = [], isLoading: pendingStoresLoading } = usePendingStores();
  const { data: adminProducts = [], isLoading: productsLoading } = useAdminProducts();
  const { data: deliveryData } = useAdminDeliveryPartners(1, 10);
  const { data: incomeSummary, isLoading: summaryLoading } = useAdminIncomeSummary();

  const orders = ordersData?.items ?? [];
  const recentOrders = orders.slice(0, 5);
  const pendingProducts = adminProducts.filter((p) => p.status === "pending");
  const activeOrders = orders.filter((o) => o.status !== "delivered" && o.status !== "cancelled").length;
  const totalRevenue = orders.reduce((sum, o) => sum + o.total, 0);
  const approvedStores = (storesData?.items ?? []).filter((s) => s.status === "approved").length;
  const isLoading = ordersLoading || storesLoading || pendingStoresLoading || productsLoading;
  const pendingCount = pendingStores.length + pendingProducts.length;

  const statusData = useMemo(
    () =>
      statusChart
        .map((s) => ({ ...s, value: orders.filter((o) => o.status === s.key).length }))
        .filter((s) => s.value > 0),
    [orders],
  );

  // Orders per day for the most recent days that have orders (list arrives newest first).
  const trendData = useMemo(() => {
    const counts = new Map<string, number>();
    orders.forEach((o) => counts.set(o.orderDate, (counts.get(o.orderDate) ?? 0) + 1));
    return Array.from(counts, ([day, count]) => ({ day, count })).slice(0, 7).reverse();
  }, [orders]);

  const earningsData = [
    { type: "Direct", value: incomeSummary?.directIncome ?? 0 },
    { type: "Level", value: incomeSummary?.levelIncome ?? 0 },
    { type: "Reward", value: incomeSummary?.rewardIncome ?? 0 },
    { type: "Cashback", value: incomeSummary?.cashbackIncome ?? 0 },
  ];

  const trendConfig = { count: { label: "Orders", color: "hsl(var(--primary))" } } satisfies ChartConfig;
  const earningsConfig = { value: { label: "Earned", color: "hsl(var(--primary))" } } satisfies ChartConfig;
  const statusConfig = Object.fromEntries(
    statusChart.map((s) => [s.key, { label: s.label, color: s.color }]),
  ) satisfies ChartConfig;

  const approvalRows = [
    ...pendingStores.slice(0, 3).map((s) => ({
      id: `s-${s.id}`,
      name: s.name,
      meta: `Store • ${s.address}`,
      to: "/admin/stores",
      image: undefined as string | undefined,
    })),
    ...pendingProducts.slice(0, 3).map((p) => ({
      id: `p-${p.id}`,
      name: p.name,
      meta: `Product • ${p.storeName}`,
      to: "/admin/products",
      image: p.image,
    })),
  ].slice(0, 3);

  return (
    // Desktop: fill the viewport under the topbar so nothing scrolls. Mobile: stack and scroll naturally.
    <div className="flex flex-col gap-3 lg:h-[calc(100dvh-7rem)] lg:min-h-[560px] xl:h-[calc(100dvh-8rem)]">
      <div className="flex shrink-0 items-baseline justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-heading text-xl font-bold tracking-tight text-foreground lg:text-2xl">Dashboard</h1>
          <p className="text-xs text-muted-foreground sm:truncate">Welcome back! Here's what's happening with your platform.</p>
        </div>
      </div>

      <div className="grid shrink-0 grid-cols-2 gap-3 lg:grid-cols-4">
        <KpiTile
          title="Total Revenue"
          value={isLoading ? "…" : inr(totalRevenue, 0)}
          icon={IndianRupee}
          tone="bg-gradient-primary text-primary-foreground"
        />
        <KpiTile
          title="Total Orders"
          value={isLoading ? "…" : (ordersData?.totalItems ?? orders.length).toLocaleString()}
          icon={ShoppingCart}
          tone="bg-accent/10 text-accent"
        />
        <KpiTile title="Active Stores" value={isLoading ? "…" : approvedStores} icon={Store} />
        <KpiTile
          title="Active Orders"
          value={isLoading ? "…" : activeOrders}
          icon={Clock}
          tone="bg-info/10 text-info"
        />
      </div>

      <div className="grid gap-3 lg:min-h-0 lg:flex-1 lg:grid-cols-12 lg:grid-rows-2">
        {/* Orders trend */}
        <Panel title="Orders by day" className="lg:col-span-5">
          <div className="h-44 lg:h-full">
            {ordersLoading ? (
              <Skeleton className="h-full w-full" />
            ) : trendData.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">No orders yet</p>
            ) : (
              <ChartContainer config={trendConfig} className="aspect-auto h-full w-full">
                <BarChart data={trendData} margin={{ top: 8, right: 4, left: -24, bottom: 0 }}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
                  <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={6} fontSize={11} />
                  <YAxis allowDecimals={false} tickLine={false} axisLine={false} width={40} fontSize={11} />
                  <ChartTooltip cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }} content={<ChartTooltipContent hideIndicator />} />
                  <Bar dataKey="count" fill="var(--color-count)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
              </ChartContainer>
            )}
          </div>
        </Panel>

        {/* Order status donut */}
        <Panel title="Order status" className="lg:col-span-3">
          <div className="flex h-44 items-center gap-3 lg:h-full">
            {ordersLoading ? (
              <Skeleton className="h-full w-full" />
            ) : statusData.length === 0 ? (
              <p className="w-full py-8 text-center text-sm text-muted-foreground">No orders yet</p>
            ) : (
              <>
                <div className="relative h-full min-w-0 flex-1">
                  <ChartContainer config={statusConfig} className="aspect-auto h-full w-full">
                    <PieChart>
                      <ChartTooltip content={<ChartTooltipContent hideLabel nameKey="key" />} />
                      <Pie
                        data={statusData}
                        dataKey="value"
                        nameKey="key"
                        innerRadius="62%"
                        outerRadius="92%"
                        paddingAngle={2}
                        stroke="hsl(var(--card))"
                        strokeWidth={2}
                      >
                        {statusData.map((s) => (
                          <Cell key={s.key} fill={s.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ChartContainer>
                  <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                    <span className="font-heading text-xl font-bold leading-none tabular-nums text-foreground">
                      {orders.length}
                    </span>
                    <span className="text-[10px] text-muted-foreground">orders</span>
                  </div>
                </div>
                <ul className="shrink-0 space-y-1.5 text-xs">
                  {statusData.map((s) => (
                    <li key={s.key} className="flex items-center gap-1.5">
                      <span className="h-2 w-2 rounded-full" style={{ background: s.color }} />
                      <span className="text-muted-foreground">{s.label}</span>
                      <span className="ml-auto pl-2 font-semibold tabular-nums text-foreground">{s.value}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Panel>

        {/* Distributed earnings */}
        <Panel
          title="Distributed earnings"
          className="lg:col-span-4"
          action={
            <span className="text-sm font-bold tabular-nums text-foreground">
              {summaryLoading ? "…" : inr(incomeSummary?.totalIncome ?? 0)}
            </span>
          }
        >
          <div className="h-40 lg:h-full">
            {summaryLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ChartContainer config={earningsConfig} className="aspect-auto h-full w-full">
                <BarChart data={earningsData} layout="vertical" margin={{ top: 4, right: 56, left: 0, bottom: 0 }}>
                  <XAxis type="number" hide />
                  <YAxis dataKey="type" type="category" tickLine={false} axisLine={false} width={64} fontSize={11} />
                  <ChartTooltip
                    cursor={{ fill: "hsl(var(--muted))", opacity: 0.5 }}
                    content={<ChartTooltipContent hideIndicator formatter={(v) => inr(Number(v))} />}
                  />
                  <Bar dataKey="value" fill="var(--color-value)" radius={[0, 4, 4, 0]} maxBarSize={18} minPointSize={2}>
                    <LabelList
                      dataKey="value"
                      position="right"
                      formatter={(v: number) => inr(v)}
                      className="fill-foreground text-[11px] font-medium"
                    />
                  </Bar>
                </BarChart>
              </ChartContainer>
            )}
          </div>
        </Panel>

        {/* Recent orders */}
        <Panel
          title="Recent orders"
          className="lg:col-span-7"
          action={
            <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" asChild>
              <Link to="/admin/orders">
                View all <ArrowRight className="ml-1 h-3 w-3" />
              </Link>
            </Button>
          }
        >
          <div className="scrollbar-hover scrollbar-hover-light h-full space-y-1.5 overflow-y-auto">
            {ordersLoading ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-lg" />)
            ) : recentOrders.length > 0 ? (
              recentOrders.map((order) => (
                <div
                  key={order.id}
                  className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5 dark:border-neutral-800"
                >
                  <div className="min-w-0">
                    <p className="truncate text-[13px] font-semibold leading-tight">{order.orderId}</p>
                    <p className="truncate text-[11px] text-muted-foreground">
                      {order.customerName} • ₹{order.total}
                    </p>
                  </div>
                  <Badge variant="outline" className={cn("shrink-0 px-2 py-0 text-[10px]", statusColor[order.status])}>
                    {order.status}
                  </Badge>
                </div>
              ))
            ) : (
              <p className="py-6 text-center text-sm text-muted-foreground">No orders yet</p>
            )}
          </div>
        </Panel>

        {/* Pending approvals + ops counts */}
        <Panel
          title="Pending approvals"
          className="lg:col-span-5"
          action={
            <Badge variant={pendingCount ? "destructive" : "secondary"} className="px-2 py-0 text-[10px]">
              {pendingCount} pending
            </Badge>
          }
        >
          <div className="flex h-full flex-col gap-2">
            <div className="scrollbar-hover scrollbar-hover-light min-h-0 flex-1 space-y-1.5 overflow-y-auto">
              {isLoading ? (
                Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-9 rounded-lg" />)
              ) : approvalRows.length > 0 ? (
                approvalRows.map((row) => (
                  <div
                    key={row.id}
                    className="flex items-center justify-between gap-2 rounded-lg border border-border px-2.5 py-1.5 dark:border-neutral-800"
                  >
                    <div className="flex min-w-0 items-center gap-2">
                      {row.image ? (
                        <img src={row.image} alt="" className="h-7 w-7 shrink-0 rounded-md object-cover" />
                      ) : (
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-secondary">
                          <Store className="h-3.5 w-3.5 text-muted-foreground" />
                        </div>
                      )}
                      <div className="min-w-0">
                        <p className="truncate text-[13px] font-semibold leading-tight">{row.name}</p>
                        <p className="truncate text-[11px] text-muted-foreground">{row.meta}</p>
                      </div>
                    </div>
                    <Button size="sm" variant="outline" className="h-6 shrink-0 px-2 text-xs" asChild>
                      <Link to={row.to}>Review</Link>
                    </Button>
                  </div>
                ))
              ) : (
                <p className="py-4 text-center text-sm text-muted-foreground">No pending approvals</p>
              )}
            </div>
            <div className="grid shrink-0 grid-cols-2 gap-2">
              <div className="flex items-center gap-2 rounded-lg bg-secondary/60 px-2.5 py-1.5 dark:bg-neutral-900">
                <Package className="h-4 w-4 text-primary" />
                <span className="font-heading text-base font-bold tabular-nums">{pendingProducts.length}</span>
                <span className="truncate text-[11px] text-muted-foreground">Products pending</span>
              </div>
              <div className="flex items-center gap-2 rounded-lg bg-secondary/60 px-2.5 py-1.5 dark:bg-neutral-900">
                <Truck className="h-4 w-4 text-accent" />
                <span className="font-heading text-base font-bold tabular-nums">{deliveryData?.totalItems ?? 0}</span>
                <span className="truncate text-[11px] text-muted-foreground">Delivery partners</span>
              </div>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default AdminDashboard;
